import { Avatar } from '@components/atoms/Avatar'
import { Icon } from '@components/atoms/Icon'
import { Typography } from '@components/atoms/Typography'
import { SlidePanel } from '@components/shell/SlidePanel'
import { ThemeSwitcher } from '@components/shell/ThemeSwitcher'
import type { User } from '../../../types/user'
import styles from './ProfilePanel.module.scss'

export interface ProfilePanelProps {
  open: boolean
  onClose: () => void
  user: User
  /** Defaults to a console.log placeholder until auth is wired up. */
  onLogout?: () => void
}

/**
 * User profile slide-in panel mounted at the AppShell level. Built on
 * top of the generic `SlidePanel` — provides the user identity row at
 * top, the theme switcher in a "Preferences" section, and a dummy
 * logout button at the bottom.
 *
 * Future scope (per the user spec): more profile sections (notification
 * preferences, language, about). Each will land as another `<section>`
 * inside this panel without changing the SlidePanel API.
 */
export function ProfilePanel({
  open,
  onClose,
  user,
  onLogout,
}: ProfilePanelProps) {
  function handleLogout() {
    if (onLogout) {
      onLogout()
    } else {
      // Placeholder — auth flow lands in a later phase.
      console.warn('[ProfilePanel] logout clicked — not implemented yet')
    }
  }

  const content = (
    <div className={styles.content}>
      {/* Identity card — avatar + name + email. */}
      <section
        aria-labelledby="profile-identity-heading"
        className={styles.identity}
      >
        <Typography
          id="profile-identity-heading"
          variant="label-sm"
          weight="semibold"
          color="tertiary"
          as="h2"
          className={styles.sectionLabel}
        >
          Signed in as
        </Typography>
        <div className={styles.identityRow}>
          <Avatar user={user} size="lg" />
          <div className={styles.identityText}>
            <Typography
              variant="body-lg"
              weight="semibold"
              color="primary"
              as="span"
              truncate
            >
              {user.name}
            </Typography>
            {user.email && (
              <Typography
                variant="body-md"
                color="secondary"
                as="span"
                truncate
              >
                {user.email}
              </Typography>
            )}
            {user.tenantId && (
              <Typography
                variant="label-sm"
                color="tertiary"
                as="span"
                truncate
              >
                {user.tenantId}
              </Typography>
            )}
          </div>
        </div>
      </section>

      {/* Preferences — theme. */}
      <section
        aria-labelledby="profile-preferences-heading"
        className={styles.section}
      >
        <Typography
          id="profile-preferences-heading"
          variant="label-sm"
          weight="semibold"
          color="tertiary"
          as="h2"
          className={styles.sectionLabel}
        >
          Theme
        </Typography>
        <ThemeSwitcher />
      </section>

      {/* Account actions — logout. */}
      <section
        aria-labelledby="profile-account-heading"
        className={styles.section}
      >
        <Typography
          id="profile-account-heading"
          variant="label-sm"
          weight="semibold"
          color="tertiary"
          as="h2"
          className={styles.sectionLabel}
        >
          Account
        </Typography>
        <button
          type="button"
          className={styles.logoutButton}
          onClick={handleLogout}
        >
          <Icon name="log-out" size={20} color="currentColor" />
          <Typography
            variant="body-md"
            weight="medium"
            color="inherit"
            as="span"
          >
            Log out
          </Typography>
        </button>
      </section>
    </div>
  )

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title="User profile"
      panelContent={content}
    />
  )
}
